import { Link } from 'react-router-dom';
import { useDeleteSample, useUpdateSample } from '@/services/sample.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Trash2Icon,
  ClipboardListIcon,
  CheckIcon,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { SampleModel } from '@/types/models';
import SampleImagePreview from '../../components/SampleImagePreview';
import { scannerCrop } from '@/lib/crop';
import ConfirmDialog from '../../components/ConfirmDialog';

interface SampleProps {
  sample: SampleModel;
  projectId: string;
  isEditing: boolean;
  onEdit: (sample: SampleModel) => void;
  onCancel: () => void;
}

export default function Sample({
  sample,
  projectId,
  isEditing,
  onEdit,
  onCancel,
}: SampleProps) {
  const { t } = useTranslation();
  const { mutate: deleteSample } = useDeleteSample();
  const { mutate: updateSample } = useUpdateSample();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editName, setEditName] = useState(sample.name);
  const [editDescription, setEditDescription] = useState(
    sample.description || '',
  );
  const [editDepth, setEditDepth] = useState(
    String(sample.topEdgeDepthCm ?? ''),
  );
  const [editTilt, setEditTilt] = useState(String(sample.tiltDeg ?? ''));
  const [editGps, setEditGps] = useState(
    sample.latitude != null && sample.longitude != null
      ? `${sample.latitude}, ${sample.longitude}`
      : '',
  );
  // Whole numbers in range, as the backend accepts them.
  const depth = parseBounded(editDepth, 0, 1000);
  const tilt = parseBounded(editTilt, 0, 90);
  const gps = parseGps(editGps);
  const invalid = depth === null || tilt === null || gps === null;

  // Escape leaves the sheet, unless the delete confirmation is up.
  useEffect(() => {
    if (!isEditing || showDeleteConfirm) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isEditing, showDeleteConfirm, onCancel]);

  const handleSave = () => {
    if (invalid) return;
    updateSample({
      projectId,
      sampleId: sample.id,
      data: {
        name: editName,
        description:
          editDescription.trim() === '' ? undefined : editDescription.trim(),
        topEdgeDepthCm: depth,
        tiltDeg: tilt,
        latitude: gps.latitude,
        longitude: gps.longitude,
      },
    });
    onCancel();
  };

  const deleteDialog = (
    <ConfirmDialog
      open={showDeleteConfirm}
      title={t('Delete sample')}
      description={t(
        'Are you sure you want to delete this sample? This action cannot be undone and will permanently delete the sample and all associated data.',
      )}
      confirmLabel={t('Delete sample')}
      onConfirm={() => {
        deleteSample({ projectId, sampleId: sample.id });
        setShowDeleteConfirm(false);
      }}
      onCancel={() => setShowDeleteConfirm(false)}
    />
  );

  // The sample's sheet: everything the card leaves out, editable where it
  // can be, and the rarer actions.
  if (isEditing) {
    return (
      <>
        <div className="h-full bg-white border border-[#2d6a4f] rounded-xl p-4 ring-2 ring-[#e8efe9]">
          <div className="space-y-4">
            {sample.scanner?.serialNumber && (
              <div className="flex items-baseline gap-2 text-xs">
                <span className="text-[#7a8077]">{t('Scanner')}</span>
                <span className="font-mono text-[#3a4339]">
                  {sample.scanner.serialNumber}
                </span>
              </div>
            )}
            <div>
              <Label>{t('Name')}</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{t('Description')}</Label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1.5"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                label={t('Depth')}
                unit="cm"
                value={editDepth}
                onChange={setEditDepth}
                invalid={depth === null}
                hint={t(
                  'Vertical depth of the top of the scanner below the soil surface.',
                )}
              />
              <TextField
                label={t('Tilt')}
                unit="°"
                value={editTilt}
                onChange={setEditTilt}
                invalid={tilt === null}
                hint={t(
                  'Angle of the scanner from the vertical (0° = upright).',
                )}
              />
            </div>
            <TextField
              label={t('GPS position')}
              value={editGps}
              onChange={setEditGps}
              invalid={gps === null}
              placeholder="48.858370, 2.294481"
              hint={t('Latitude, longitude in decimal degrees.')}
            />
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={invalid}
                className="bg-[#2d6a4f] hover:bg-[#245741] text-white"
              >
                <CheckIcon className="h-3.5 w-3.5 mr-1" />
                {t('Save changes')}
              </Button>
              <Button size="sm" variant="ghost" onClick={onCancel}>
                <XIcon className="h-3.5 w-3.5 mr-1" />
                {t('Cancel')}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                className="ml-auto h-8 w-8 shrink-0 text-[#7a8077] hover:text-red-600 hover:bg-red-50"
                title={t('Delete sample')}
              >
                <Trash2Icon className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
        {deleteDialog}
      </>
    );
  }

  return (
    <>
      <Link
        to={`${sample.id}`}
        // A compact block: the name across the top, the thumbnail under it,
        // then the description. Its width is the grid's to set; full height,
        // so a row's cards line up.
        className="group relative block h-full bg-white border border-[#e3e5df] rounded-xl p-3 shadow-[0_1px_2px_rgba(28,38,32,0.05)] hover:border-[#b7cfc0] hover:shadow-[0_4px_14px_rgba(28,38,32,0.08)] transition"
      >
        {/* The name over the whole width, above the thumbnail, and whole:
            it wraps rather than being cut. */}
        <div className="mb-2">
          <h3 className="text-[15px] leading-snug font-semibold text-[#1c2620] break-words">
            {sample.name}
          </h3>
          {/* Over the card's corner, on hover only, so the name has the
              card's width; always there without a pointer to hover. */}
          {projectId && (
            <div
              className="absolute right-2 top-2 flex rounded-md bg-white/95 opacity-0 shadow-sm ring-1 ring-[#ecede8] transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100"
              onClick={(e) => e.preventDefault()}
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.preventDefault();
                  onEdit(sample);
                }}
                className="h-7 w-7 text-[#7a8077] hover:text-[#1c2620] hover:bg-[#f0f2ee]"
                title={t('Sample details')}
              >
                <ClipboardListIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
        {/* The card's whole width; the details are on the sample's sheet. */}
        <SampleImagePreview
          projectId={projectId}
          sampleId={sample.id}
          rotateClockwise={sample.rotateClockwise}
          crop={scannerCrop(sample.scanner)}
        />
        {/* Under the thumbnail, so a row's thumbnails line up whatever the
            descriptions' lengths, and the slack falls at the cards' foot. */}
        {sample.description && (
          <p
            className="text-xs text-[#5a6259] leading-relaxed line-clamp-3 mt-2"
            title={sample.description}
          >
            {sample.description}
          </p>
        )}
      </Link>
    </>
  );
}

/** A whole number typed as text, or null when it isn't one in [min, max]. */
function parseBounded(text: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  const n = Number(text.trim());
  return n >= min && n <= max ? n : null;
}

/** "lat, lng" in decimal degrees (comma, semicolon or space between), as
 *  GPS and map apps copy it. Empty means no position; null means invalid. */
function parseGps(
  text: string,
): { latitude: number | null; longitude: number | null } | null {
  if (text.trim() === '') return { latitude: null, longitude: null };
  const m = text
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const latitude = Number(m[1]);
  const longitude = Number(m[2]);
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { latitude, longitude };
}

function TextField({
  label,
  unit,
  value,
  onChange,
  invalid,
  hint,
  placeholder,
}: {
  label: string;
  unit?: string;
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
  hint: string;
  placeholder?: string;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative mt-1.5">
        <Input
          type="text"
          inputMode={unit ? 'numeric' : 'decimal'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-invalid={invalid}
          className={`${unit ? 'pr-9' : ''} tabular-nums ${invalid ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
        />
        {unit && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#7a8077]">
            {unit}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-[#7a8077]">{hint}</p>
    </div>
  );
}
